"use client";

import type React from "react";
import { motion } from "@/lib/motion";
import { CardWave, waveSeed } from "@/components/ui";

/* Flagship feature card — the wave-backed shell the homepage's Why Proxidize
   section uses for its two headline features, available to the product pages.

   The point of this card is the mock it wraps. A feature grid whose visuals are
   176px chip clusters reads as four containers with something small inside; the
   same claim over a full product panel reads as evidence. Use it for the one or
   two features on a page that can carry a real panel, and the plain
   ui/FeatureCard for the supporting ones — the contrast between the two is the
   hierarchy.

   Only the CONTENT animates in; the tile keeps its own surface, so a card never
   appears as an empty box that fills in afterwards. */

const cardVariants = {
  hidden: { opacity: 0, y: 16 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.45 } },
};

const staticVariants = {
  hidden: { opacity: 1, y: 0 },
  visible: { opacity: 1, y: 0 },
};

interface Props {
  title: string;
  description: string;
  /** The product panel this card exists to frame. */
  children: React.ReactNode;
  /** Caller's `useReducedMotion()` — the section already reads it once. */
  reduced: boolean | null;
}

export default function FlagshipCard({ title, description, children, reduced }: Props) {
  return (
    <article className="bg-card border-border relative isolate flex flex-col overflow-hidden rounded-2xl border p-6 sm:p-8">
      <CardWave seed={waveSeed(title)} />
      <motion.div
        variants={reduced ? staticVariants : cardVariants}
        className="relative z-10 flex flex-1 flex-col"
      >
        <h3 className="text-foreground text-[19px] font-semibold tracking-[-0.01em]">{title}</h3>
        <p className="text-muted-foreground mt-1.5 max-w-md text-sm leading-relaxed">
          {description}
        </p>
        {/* items-end so the panel sits on the card's floor: the two cards in a
            row have different copy lengths, and aligning the panels to the
            bottom keeps them level with each other. */}
        <div className="mt-6 flex flex-1 items-end">{children}</div>
      </motion.div>
    </article>
  );
}
